// src/store/slices/clientsSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  createClient,
  fetchClients,
  fetchStreamlinedClients,
  fetchClientById,
  updateClient,
  updateClientClinician,
  deleteClient,
  fetchParents,
  fetchClientsByClinicianId,
} from "../thunks/clientsThunk";

const clientsSlice = createSlice({
  name: "clients",
  initialState: {
    clients: [],
    parents: [],
    status: "idle",
    error: null,
    selectedClient: null,
    pagination: {
      totalClients: 0,
      currentPage: 1,
      totalPages: 1,
      pageSize: 20,
    },
  },
  reducers: {
    setClient: (state, action) => {
      state.selectedClient = action.payload;
    },
    getClient: (state, action) => {
      return state.selectedClient;
    },
    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },
    clearParents: (state) => {
      state.parents = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Client
      .addCase(createClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clients.push(action.payload);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch All Clients (Full Data)
      .addCase(fetchClients.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clients = action.payload.clients;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch All Clients (Streamlined)
      .addCase(fetchStreamlinedClients.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchStreamlinedClients.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clients = action.payload.clients;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchStreamlinedClients.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch Client by ID
      .addCase(fetchClientById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedClient = action.payload;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update Client
      .addCase(updateClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.clients.findIndex(
          (client) => client._id === action.payload.client._id
        );
        if (index !== -1) {
          state.clients[index] = action.payload.client;
        }
        state.selectedClient = action.payload.client;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Delete Client
      .addCase(deleteClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clients = state.clients.filter(
          (client) => client._id !== action.meta.arg
        );
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch Parents
      .addCase(fetchParents.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchParents.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.parents = action.payload;
      })
      .addCase(fetchParents.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update Client Clinician
      .addCase(updateClientClinician.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateClientClinician.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedClient = action.payload.client;
      })
      .addCase(updateClientClinician.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { setClient, getClient, clearSelectedClient, clearParents } =
  clientsSlice.actions;
export default clientsSlice.reducer;
